import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, Typography } from 'antd';
import {
  BookOutlined,
  CheckCircleOutlined,
  HistoryOutlined,
  MailOutlined,
  MobileOutlined,
  NotificationOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import config from '../../assets/utils/config';

const { Text, Paragraph, Title } = Typography;

const STORAGE_BASE = 'campaigns-howto-seen-v1';

const readUserId = () => {
  try {
    const json = localStorage.getItem(config.key.user);
    if (!json) return null;
    const user = JSON.parse(json);
    return user?.id != null ? String(user.id) : null;
  } catch {
    return null;
  }
};

const storageKeyForUser = () => {
  const userId = readUserId();
  return userId ? `${STORAGE_BASE}:${userId}` : STORAGE_BASE;
};

const hasAcknowledged = () => {
  try {
    return localStorage.getItem(storageKeyForUser()) === '1';
  } catch {
    return false;
  }
};

const persistAcknowledged = () => {
  try {
    localStorage.setItem(storageKeyForUser(), '1');
  } catch {
    // ignore quota / private mode
  }
};

const GUIDE_SECTIONS = [
  {
    key: 'what',
    icon: <NotificationOutlined />,
    title: 'What Communications is',
    body: (
      <>
        <Paragraph>
          Communications is the unified place to send <Text strong>push notifications</Text> and{' '}
          <Text strong>email</Text> — together or separately — from one compose flow.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          Use the <Text strong>Compose</Text> tab to build a campaign, and <Text strong>History</Text> to
          review past push and email sends.
        </Paragraph>
      </>
    ),
  },
  {
    key: 'compose',
    icon: <CheckCircleOutlined />,
    title: 'How to compose',
    body: (
      <ol className="campaigns-howto__steps">
        <li>
          <Text strong>Channels</Text> — turn on Push, Email, or both.
        </li>
        <li>
          <Text strong>Type</Text> — System, Marketing, Maintenance, or Emergency.
        </li>
        <li>
          <Text strong>Email audience</Text> (when Email is on) — pick audiences and optional filters,
          then use <Text strong>Estimate recipients</Text> to check reach.
        </li>
        <li>
          <Text strong>Message</Text> — title/subject and body. Live preview updates as you type.
        </li>
        <li>
          <Text strong>Schedule</Text> (optional) — leave empty to send immediately, or pick a time.
        </li>
        <li>
          Confirm in the dialog, then send, schedule, or save as draft.
        </li>
      </ol>
    ),
  },
  {
    key: 'channels',
    icon: <MobileOutlined />,
    title: 'Push vs email',
    body: (
      <div className="campaigns-howto__compare">
        <div className="campaigns-howto__compare-card is-push">
          <div className="campaigns-howto__compare-head">
            <MobileOutlined /> Push
          </div>
          <ul>
            <li>Goes to <Text strong>all active client-app devices</Text></li>
            <li>Not segmented by audience or filters</li>
            <li>Title max 60 chars; body max 250 (plain text)</li>
            <li>Marketing type cannot use Push</li>
            <li>Skipped when you save as draft</li>
          </ul>
        </div>
        <div className="campaigns-howto__compare-card is-email">
          <div className="campaigns-howto__compare-head">
            <MailOutlined /> Email
          </div>
          <ul>
            <li>Segmented by audience (clients, admins, enrollees, etc.)</li>
            <li>Optional filters (tier, age, org, and more)</li>
            <li>HTML or plain text body supported</li>
            <li>Can save as draft without sending</li>
            <li>Estimate recipients before you send</li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    key: 'history',
    icon: <HistoryOutlined />,
    title: 'History',
    body: (
      <>
        <Paragraph>
          The History tab lists recent push and email campaigns together. Filter by channel, refresh,
          and cancel items that are still <Text code>draft</Text>, <Text code>scheduled</Text>, or{' '}
          <Text code>sending</Text>.
        </Paragraph>
        <Paragraph style={{ marginBottom: 0 }}>
          After a successful send, you are taken to History automatically.
        </Paragraph>
      </>
    ),
  },
  {
    key: 'caveats',
    icon: <WarningOutlined />,
    title: 'Important caveats',
    body: (
      <ul className="campaigns-howto__bullets">
        <li>
          <Text strong>Push is unsegmented</Text> — every active client device gets it. Confirm carefully.
        </li>
        <li>
          <Text strong>Marketing + Push</Text> is blocked; use email only or change the type.
        </li>
        <li>
          <Text strong>Emergency</Text> campaigns are high priority and go out on selected channels right away
          (unless scheduled).
        </li>
        <li>
          <Text strong>Draft</Text> only applies to email; push is not created for drafts.
        </li>
        <li>
          Recipient estimates apply to <Text strong>email only</Text>.
        </li>
      </ul>
    ),
  },
];

/**
 * First-visit how-to modal + reopen control for Campaigns.
 * Persistence: localStorage key `campaigns-howto-seen-v1` or `campaigns-howto-seen-v1:<userId>`.
 */
export const useCampaignsHowTo = () => {
  const [open, setOpen] = useState(false);
  const [required, setRequired] = useState(false);

  useEffect(() => {
    if (!hasAcknowledged()) {
      setRequired(true);
      setOpen(true);
    }
  }, []);

  const openGuide = useCallback(() => {
    setOpen(true);
  }, []);

  const acknowledge = useCallback(() => {
    persistAcknowledged();
    setRequired(false);
    setOpen(false);
  }, []);

  const closeOptional = useCallback(() => {
    if (required) return;
    setOpen(false);
  }, [required]);

  return { open, required, openGuide, acknowledge, closeOptional };
};

export const CampaignsHowToButton = ({ onClick }) => (
  <Button icon={<BookOutlined />} onClick={onClick}>
    How to
  </Button>
);

export const CampaignsHowToModal = ({ open, required, onAcknowledge, onClose }) => {
  const footer = useMemo(
    () => [
      <Button
        key="ack"
        type="primary"
        size="large"
        icon={<CheckCircleOutlined />}
        onClick={onAcknowledge}
        className="campaigns-howto__ack-btn"
      >
        {required ? 'I understand — continue to Communications' : 'Got it'}
      </Button>,
    ],
    [onAcknowledge, required],
  );

  return (
    <Modal
      open={open}
      title={null}
      footer={footer}
      onCancel={onClose}
      closable={!required}
      maskClosable={!required}
      keyboard={!required}
      width={720}
      centered
      destroyOnClose
      className="campaigns-howto-modal"
      styles={{ body: { paddingTop: 8 } }}
    >
      <div className="campaigns-howto">
        <div className="campaigns-howto__hero">
          <span className="campaigns-howto__hero-icon" aria-hidden>
            <BookOutlined />
          </span>
          <div>
            <Title level={4} className="campaigns-howto__title">
              How to use Campaigns
            </Title>
            <Text type="secondary">
              {required
                ? 'Please read this short guide before composing your first campaign.'
                : 'Quick reference for push and email campaigns.'}
            </Text>
          </div>
        </div>

        <div className="campaigns-howto__sections">
          {GUIDE_SECTIONS.map((section) => (
            <section key={section.key} className="campaigns-howto__section">
              <div className="campaigns-howto__section-label">
                <span className="campaigns-howto__section-icon">{section.icon}</span>
                {section.title}
              </div>
              <div className="campaigns-howto__section-body">{section.body}</div>
            </section>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default CampaignsHowToModal;
